import { useCallback, useState, useSyncExternalStore } from "react";
import { pushService } from "@/services";
import { requestFcmToken } from "@safers/pwa";

let registered = false;
const registeredListeners = new Set<() => void>();

function subscribeRegistered(callback: () => void) {
  registeredListeners.add(callback);
  return () => {
    registeredListeners.delete(callback);
  };
}

function getRegisteredSnapshot() {
  return registered;
}

function markRegistered(value: boolean) {
  if (registered === value) return;
  registered = value;
  for (const l of registeredListeners) l();
}

export function resetPushSubscription() {
  markRegistered(false);
}

interface UsePushSubscriptionReturn {
  subscribed: boolean;
  busy: boolean;
  subscribe: () => Promise<boolean>;
}

export function usePushSubscription(siteId: number | null): UsePushSubscriptionReturn {
  const subscribed = useSyncExternalStore(
    subscribeRegistered,
    getRegisteredSnapshot,
    getRegisteredSnapshot
  );
  const [busy, setBusy] = useState(false);

  const subscribe = useCallback(async () => {
    setBusy(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
      if (!("Notification" in window)) return false;
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") return false;
      }
      const reg = await navigator.serviceWorker.ready;
      const token = await requestFcmToken(reg);
      if (!token) return false;
      await pushService.registerFcmToken(token, siteId);
      markRegistered(true);
      return true;
    } catch {
      return false;
    } finally {
      setBusy(false);
    }
  }, [siteId]);

  return { subscribed, busy, subscribe };
}
